import isEmpty from 'lodash/isEmpty'
import React, { useState } from 'react'

import { ProductTileV2 } from '@Components/ProductList/ProductTiles/LoadMore/ProductTileV2/ProductTileV2'
import { ProductTileWrapper } from '@Components/ProductList/ProductTileWrapper'
import { SkeletonList } from '@Components/Skeleton/Skeleton'
import { PromptDialog } from '@Domains/Cart/Components/Prompt/Dialog'
import { useAddToCart } from '@Domains/Cart/Hooks/useAddToCart'
import { useChipAssetsV2 } from '@Domains/Product/Features/Display/ChipAssets'
import { getProductAsset } from '@Domains/Product/Features/ProductSlider/ProductSlider'
import { useProductTactics } from '@Domains/Product/Features/Tactics/Hook'
import { Product } from '@Generated/graphql'

import styles from './grid.module.scss'

export const RecommendationGrid = ({
  group,
  id,
  productID,
}: {
  group: Exclude<keyof NonNullable<Product['tactics']>, '__typename'>
  id: string
  productID: string
}) => {
  const [ref, loading, tactics] = useProductTactics(productID, group)
  const getChipsForProduct = useChipAssetsV2()
  const [addToCart, { isLoading, prompts, reset, variables }] = useAddToCart()
  const [addingToCart, setAddingToCart] = useState<string | null>(null)
  const [addedToCart, setAddedToCart] = useState<string[]>([])
  if (loading) {
    return (
      <div className={styles.grid} ref={ref}>
        <h3 className={styles.gridHeader}>{' '}</h3>
        <ProductTileWrapper maxColumns={8}>
          <SkeletonList count={25} />
        </ProductTileWrapper>
      </div>
    )
  }

  const tactic = tactics?.find(i => i.id === id)
  const recommendation = ((tactic as any)?.recommendations || [])?.[0]

  if (!recommendation?.products || recommendation.products.length === 0) {
    return <div ref={ref} />
  }

  const sourceID = `product_page_${recommendation.placement}_${recommendation.id}`

  return (
    <div className={styles.grid} data-in-view={sourceID} ref={ref}>
      <h3 className={styles.gridHeader}>{recommendation.title}</h3>

      <ProductTileWrapper maxColumns={8}>
        {recommendation.products.map((product) => {
          const { bottomChips, topChips } = getChipsForProduct(product)
          return (
            <ProductTileV2
              key={`recommendation_${product.id}`}
              alreadyAddedToCart={addedToCart.includes(product.id)}
              bottomChips={bottomChips}
              disableBuyButton={false}
              isOutOfStock={!!product.availability && !product.availability.isInStock}
              loadingBuyButton={isLoading && addingToCart === product.id}
              onBuy={
                 () => {
                   setAddingToCart(product.id)
                   addToCart([
                     {
                       productID: product.id,
                       quantity: 1,
                     },
                   ], {
                     sourceID: `product_page_${recommendation?.placement}_${recommendation?.id}`,
                   })
                     .then((r) => {
                       if (isEmpty(r?.errors)) {
                         setAddedToCart(prev => [...prev, product.id])
                       }
                     })
                 }
              }
              originalPrice={product.masterVariant?.price?.format || ''}
              overwrittenPrice={product.masterVariant?.price?.discount?.format || undefined}
              primaryImage={getProductAsset(product)}
              productID={product.id}
              sourceID={sourceID}
              title={product.name || ''}
              topChips={topChips}
              url={product.url || ''}
            />
          )
        })}
      </ProductTileWrapper>
      {prompts && prompts.length !== 0 && (
        <PromptDialog
          isLoading={isLoading}
          onClose={reset}
          onSubmit={(a, c) => addToCart(a, { ...c, sourceID: `product_page_${recommendation?.placement}_${recommendation?.id}` })}
          prompts={prompts}
          promptsOrigin={variables}
        />
      )}
    </div>
  )
}
